/* helper.ts */

/* Types */
import { SVGSelection } from "src/shared/types/d3";

/* Interfaces */
import { ChartDimensionsProps } from "src/shared/interfaces/charts/BaseChart";
import { BasicProps } from "src/shared/interfaces/custom-cohorts/CustomCohorts";
import { FilterProps } from "src/shared/interfaces/insights-filter/InsightsFilter";

interface MarginProps {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const computePlotHeightWidth = (
  dimensions: ChartDimensionsProps,
  margin: MarginProps
) => {
  const { height: dimensionHeight, width: dimensionWidth } =
    dimensions;

  const height =
    dimensionHeight - margin.top - margin.bottom;
  const width = dimensionWidth - margin.left - margin.right;

  return { margin, height, width };
};

export const createViewBox = (
  svg: SVGSelection,
  dimensions: ChartDimensionsProps
) => {
  const { height, width } = dimensions;
  return svg.attr("viewBox", `0 0 ${width} ${height}`);
};

/**
 * Extract field 'unit' from 'featureTypes'.
 *
 * @param {BasicProps[]} data
 * @param {FilterProps<string>} featuresOrCohort - can be either 'features' or 'cohorts'.
 * @return {string | undefined} - Returns the 'unit' of the feature or cohort if found, otherwise undefined.
 */
export const getUnitFromFeatureOrCohort = (
  data?: BasicProps[],
  featuresOrCohort?: FilterProps<string>
): string | undefined => {
  return data?.find(
    (featureOrCohort) =>
      featureOrCohort.entity?.name ===
      featuresOrCohort?.value
  )?.unit;
};

export const splitAxisLabel = (unit?: string) => {
  if (!unit) return { xLabel: "", yLabel: "" };

  const [xLabel, yLabel] = unit
    .split("|")
    .map((s) => s.trim());

  return {
    xLabel: xLabel || "",
    yLabel: yLabel || "",
  };
};

/**
 * Strips characters that are invalid or problematic in file names across
 * Windows, macOS, and Linux (<>:"/\|?* and C0 control characters), removes
 * trailing dots and spaces (Windows rejects these), guards against Windows
 * reserved device names (CON, NUL, COM1…), and falls back to a safe default
 * when the result would otherwise be empty or reserved.
 */
export const sanitizeFilename = (
  name: string,
  fallback: string
): string => {
  const sanitized = name
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001F]+/g, "-")
    .replace(/\s+/g, " ")
    .replace(/[. ]+$/g, "")
    .trim();

  const stem = sanitized.split(".")[0];

  return sanitized &&
    !/^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i.test(stem)
    ? sanitized
    : fallback;
};
